import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import type { LadderBrief } from "@/lib/types";
import { formatBannerCountdown } from "@/pages/ladder/utils";

const MAX_ATTEMPTS = 3;

// 메인 진입 시 진행중 사다리게임이 있으면 중앙 팝업으로 노출한다.
// 진행중 게임이 없거나 로딩 중이면 스켈레톤/빈 카드 없이 통째로 사라진다.
export default function LadderBanner() {
  const [data, setData] = useState<LadderBrief[] | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(true);
  const reloadedRef = useRef(false);

  // 첫 진입에서 동시 요청/일시적 네트워크 오류로 실패해도 팝업이 조용히 사라지지 않도록
  // 몇 번 재시도한다. 진행중 게임이 없으면 재시도 후 그냥 null 을 유지한다.
  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let attempt = 0;

    const load = () => {
      apiGet<LadderBrief[]>("ladders.php")
        .then((res) => {
          if (cancelled) return;
          setData((res.data as LadderBrief[]) ?? []);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          attempt += 1;
          if (attempt < MAX_ATTEMPTS) {
            timer = window.setTimeout(load, 800 * attempt);
          } else {
            if (import.meta.env.DEV) {
              console.error("[LadderBanner] ladders.php 불러오기 실패:", err);
            }
            setData([]);
          }
        });
    };

    load();

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [reloadKey]);

  const active = useMemo(() => {
    const open = (data ?? []).filter((g) => g.state === "open");
    if (open.length === 0) return null;
    // 동시에 여러 개가 진행중이면 가장 먼저 끝나는(end_in 최소) 게임 하나만 노출
    return [...open].sort((a, b) => a.end_in - b.end_in)[0];
  }, [data]);

  // 진행중 게임이 생기면 남은 초를 초기화하고, 받은 시점부터 1초씩 감산.
  // 사용자 PC 시계(Date.now)를 믿지 않고 end_in 을 직접 감산한다.
  useEffect(() => {
    if (!active) {
      setRemaining(null);
      return;
    }
    setRemaining(active.end_in);
    reloadedRef.current = false;
    const timer = window.setInterval(() => {
      setRemaining((prev) => (prev === null ? null : Math.max(0, prev - 1)));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [active]);

  // 0 이 되면 목록을 다시 불러와 종료됐는지 확인 (종료 시 팝업이 사라짐)
  useEffect(() => {
    if (remaining === 0 && !reloadedRef.current) {
      reloadedRef.current = true;
      setReloadKey((k) => k + 1);
    }
  }, [remaining]);

  const show = open && !!active;

  // ESC 로 닫기
  useEffect(() => {
    if (!show) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [show]);

  // 팝업이 떠 있는 동안 뒤 배경 스크롤 잠금
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [show]);

  if (!active || !open) return null;

  const joined = active.is_joined === 1;
  const cd = formatBannerCountdown(remaining ?? active.end_in);
  const close = () => setOpen(false);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ladder-popup-title"
    >
      <div
        className="absolute inset-0 bg-primary-950/60"
        onClick={close}
        aria-hidden="true"
      ></div>

      <div className="relative z-10 w-full max-w-sm overflow-hidden rounded-xl bg-background-50 shadow-xl">
        {/* 금색 헤더 */}
        <div className="relative bg-gradient-to-r from-accent-500 to-accent-400 px-5 pb-5 pt-6 text-primary-950">
          <button
            type="button"
            onClick={close}
            aria-label="팝업 닫기"
            className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-primary-950/15 text-primary-950 transition-colors hover:bg-primary-950/25"
          >
            <i className="ri-close-line text-lg" aria-hidden="true"></i>
          </button>

          <span
            className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-950/10 text-2xl animate-pulse"
            aria-hidden="true"
          >
            <i className="ri-dice-line"></i>
          </span>

          <p className="mt-3 text-xs font-bold tracking-wide text-primary-900/80">
            진행중인 사다리게임
          </p>
          <h3
            id="ladder-popup-title"
            className="mt-1 font-heading text-lg font-bold leading-snug"
          >
            {active.subject || active.title || "사다리게임"}
          </h3>
        </div>

        {/* 본문 */}
        <div className="px-5 py-5">
          <div className="flex items-center justify-between rounded-lg bg-background-100 px-4 py-3">
            <span className="text-sm text-foreground-600">종료까지</span>
            <span className="flex items-center gap-2">
              <span
                className={`tabular-nums font-extrabold ${
                  cd.urgent ? "text-accent-700" : "text-foreground-950"
                }`}
              >
                {cd.text}
              </span>
              {cd.urgent && (
                <span className="whitespace-nowrap rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-bold text-accent-900">
                  마감 임박
                </span>
              )}
            </span>
          </div>

          <div className="mt-3 flex items-center justify-between rounded-lg bg-background-100 px-4 py-3">
            <span className="text-sm text-foreground-600">남은 칸</span>
            <span className="flex items-center gap-2">
              <span className="font-extrabold tabular-nums text-foreground-950">
                {active.left_cnt}칸
              </span>
              {joined && (
                <span className="whitespace-nowrap rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-bold text-primary-700">
                  참여완료
                </span>
              )}
            </span>
          </div>

          <Link
            to={`/ladder/${active.no}`}
            onClick={close}
            className="mt-5 flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
          >
            {joined ? "결과 보기" : "참여하기"}
            <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
          </Link>

          <button
            type="button"
            onClick={close}
            className="mt-2 w-full whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-700"
          >
            나중에
          </button>
        </div>
      </div>
    </div>
  );
}